// @erp/ui/sidebar — the drawer-side nav content, ported from
// ../shippra/apps/libms/src/components/layout/Sidebar.tsx: the same 224px
// column and the same logo / institution / search / nav / account stack.
//
// WHAT THE CODE ACTUALLY RENDERS (header corrected 2026-09-16, audit L26 — the
// earlier text quoted the SOURCE's 13/12/10px metrics and its literal grey
// ink, none of which survive here):
//   • Type is on the Spectrum ramp: EVERY navigation row — parent, child, and
//     the area lists under "Areas"/"Other areas" — is `text-sm` (14px) in a
//     32px box with 4px corners, which is what Adobe's own product ships
//     (measured 2026-10-02). The area lists were the leftover: they kept the
//     old 26px/12px/9px recipe for a day after the rows above them moved.
//   • (historical) parent and child rows were `text-xs` (12px), the
//     section labels `text-detail-s` (11px) — the source's 13px and 10px are not
//     ramp steps and were snapped when `--text-*` was redefined (Law F5).
//   • Corners are named steps: parent rows `rounded-600` (9px, the ported libms
//     radius, which IS a Spectrum step), child rows `rounded-500` (8px), the
//     search field `rounded-300`.
//   • Rows are router <Link>s over a permission-filtered NavModule[], not
//     onNavigate(id) callbacks over a static NAV_SECTIONS — mini's nav is
//     multi-module and grant-filtered, and the module switcher stays.
//   • INK is OPAQUE, on the text-role stops (DEV-GUIDE-002 §12.2; migrated
//     2026-09-16, audit batch B4 / UX-12). The source painted every secondary
//     element as `rgba(0,0,0,α)` at 0.22–0.65, and the first port rewrote that
//     as `base-content/[α]` — an alpha ink that measured 1.57–5.00:1 and
//     failed axe's colour-contrast on every page. Now, by duty: inactive
//     labels and the section eyebrows = `text-base-muted` (gray-700, 9.44:1 on
//     base-100 light / 10.93 darkest); icons, chevrons and the search
//     placeholder = `text-base-subtle` (gray-600 — the icon/meta stop, 5.17 /
//     7.77); the kbd chip = `bg-base-300` with `text-base-muted` (gray-700 on
//     gray-200, 7.56 / 8.56 — §12.2 keeps gray-600 OFF the band); dividers =
//     `border-base-300` (the gray-200 decorative stop); the search field's
//     ground = `bg-base-200` + `border-base-300` (layer-1 on layer-2). The
//     selected row keeps the primary-alpha ground — owner decision D11, the
//     one sanctioned selection transparency (base.css --menu-active-bg) — and
//     its label stays `base-content`. The section eyebrow is `.text-detail-s`
//     alone (font-size-50, 700, Adobe's 0.06em letter-spacing); the former
//     hand-picked 0.08em tracking utility duplicated what the class already owned.
import { For, Show, createEffect, createMemo, createSignal } from "solid-js";
import { Dynamic } from "solid-js/web";
import { Link } from "@tanstack/solid-router";
import { Building2, Home, LogOut } from "lucide-solid";
import type { NavItem, NavModule } from "@erp/auth-kit/nav";
import { resolveActiveItem, resolveActiveModule } from "@erp/auth-kit/nav";
import { avatarTone } from "../avatar-photo/index.js";

/* Which item row lights is the kit's `resolveActiveItem` (auth-kit/nav): the
   longest matching leaf, and for a HIDDEN leaf — a detail or utility route the
   menu omits — its nearest visible ancestor, so a book's edit page still lights
   Titles while "Record offline payment" lights nothing and merely opens Billing. */

/**
 * A two-level item — the source's expand/collapse parent row plus the indented
 * child rail. Open state: an active child expands its parent by default; a
 * manual toggle then wins until the active child changes the default again.
 */

export interface SidebarUser {
  name: string;
  email?: string;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return parts.length >= 2
    ? ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase()
    : name.substring(0, 2).toUpperCase() || "?";
}

/**
 * Which rail item's children the panel shows: the active one, and only when it
 * has any. Module-scope because the two bands now live in different parts of
 * the shell — the rail is chrome, the panel is INSIDE the content — and they
 * must agree without passing state between them.
 */
export function resolvePanelParent(pathname: string, modules: NavModule[]): NavItem | undefined {
  const mod = resolveActiveModule(pathname, modules);
  if (!mod) return undefined;
  /* NO PANEL ON A PAGE THAT IS ALREADY AN INDEX (2026-10-02, the owner): the
     module's home and an area's own landing both exist to LIST their screens —
     a card grid of exactly the links the panel would repeat. Showing both says
     the same thing twice and makes the home look like it is missing something.
     Adobe's /home behaves this way too: the rail stays, the panel is simply not
     there, and the content takes the width.
     Deeper than a landing, the panel earns its place: that is where the page is
     one screen rather than a directory. */
  if (pathname === mod.to) return undefined;
  const activeId = resolveActiveItem(pathname, mod);
  const parent = mod.sections
    .flatMap((sec) => sec.items.filter((i) => !i.hidden))
    .find(
      (i) =>
        (activeId === i.id || (i.children ?? []).some((c) => c.id === activeId)) &&
        (i.children ?? []).some((c) => !c.hidden),
    );
  // The area's OWN landing (/library/catalogue) is an index too — the owner's
  // example: "catalogue has an index page, so no submenu in the catalogue".
  return parent && pathname === parent.to ? undefined : parent;
}

/**
 * The SECOND band, rendered INSIDE the main content region — which is where
 * Adobe puts it (measured 2026-10-02: their `.left-panel` sits inside
 * `fw-body`, while the rail sits outside it, and the panel carries the
 * content's own white ground with no divider). The shell mounts this to the
 * left of the page; it is not part of the sidebar's drawer.
 */
export function SidebarPanel(props: {
  /** The portal's permission-filtered modules — the same list the rail reads. */
  modules: NavModule[];
  /** The current URL; decides which area's children the panel shows, if any. */
  pathname: string;
  /** The tenant line under the area's name — omitted when a portal has none. */
  institutionName?: string;
  /** Layout classes from the shell (its sticky/rounded treatment inside main). */
  class?: string;
}) {
  const parent = createMemo(() => resolvePanelParent(props.pathname, props.modules));
  const activeId = createMemo(() => resolveActiveItem(props.pathname, resolveActiveModule(props.pathname, props.modules)));
  return (
    <Show when={parent()}>
      {(p) => (
        <nav
          aria-label={`${p().label} sections`}
          class={`flex w-60 shrink-0 flex-col ${props.class ?? ""}`}
        >
          <div class="shrink-0 px-4 pb-1 pt-3">
            <span class="text-detail-s block truncate text-base-muted">{p().label}</span>
            <Show when={props.institutionName}>
              <span class="mt-0.5 flex items-center gap-1 truncate text-xs text-base-subtle">
                <Building2 size={11} aria-hidden="true" class="shrink-0" />
                {props.institutionName}
              </span>
            </Show>
          </div>
          {/* 16px inset and a 4px GAP between rows (2026-10-02, the owner: a
              hover under the selected row made one unbroken block). An earlier
              pass read their pitch as 32 and butted the rows together — that
              measured the inner <a>, not the <li> around it, which carries
              `margin: 4px 0`. Their true pitch is 36, and the 4px is exactly
              what keeps two highlighted boxes from touching. */}
            <div class="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto overflow-x-clip px-4 pb-2">
            <For each={(p().children ?? []).filter((c) => !c.hidden)}>
              {(child) => (
                <Link
                  to={child.to}
                  activeOptions={{ exact: true }}
                  aria-current={activeId() === child.id ? "page" : undefined}
                  class={`flex h-8 w-full items-center gap-2 rounded-100 px-3 text-left text-sm transition-all ${
                    activeId() === child.id
                      ? "bg-(--ui-state-down) text-base-content"
                      : "text-base-muted hover:bg-(--ui-state-hover) focus-visible:bg-(--ui-state-hover) active:bg-(--ui-state-down)"
                  }`}
                >
                  {/* THE ROW'S ICON (2026-10-02, the owner: "use icons in this
                      sub menu just like adobe"). Every nav child already
                      carried one — 38 in the lib manifest alone — the panel
                      just was not drawing it. 20px and inheriting the row's
                      ink, which is what theirs measures: a 20x20 glyph at the
                      12px padding, filled in the same grey as the label. */}
                  <Show when={child.icon}>
                    <span class="flex shrink-0">
                      <Dynamic component={child.icon!} size={20} />
                    </span>
                  </Show>
                  <span class="min-w-0 truncate">{child.label}</span>
                </Link>
              )}
            </For>
          </div>
        </nav>
      )}
    </Show>
  );
}

export function Sidebar(props: {
  modules: NavModule[];
  pathname: string;
  class?: string;
  /** The tenant line under the logo — hidden when absent (mini has no tenant concept yet). */
  institutionName?: string;
  /** The signed-in account. Null/absent ⇒ no account row (signed-out chrome). */
  user?: SidebarUser | null;
  onSignOut?: () => void | Promise<void>;
  onProfile?: () => void;
  /**
   * Drop the in-sidebar module lists ("Areas" / "Other areas"). AppShell sets
   * it whenever it renders the module STRIP above the header, so the two bands
   * never offer the same jump twice; a shell with no strip (one module, or the
   * sidebar used on its own) keeps them.
   */
  hideAreaLists?: boolean;
}) {
  const active = createMemo(() => resolveActiveModule(props.pathname, props.modules));
  const activeId = createMemo(() => resolveActiveItem(props.pathname, active()));
  // Sign-out is a server round-trip; without feedback the control reads as
  // "lagging" (the reported bug when this lived in the header).
  const [signingOut, setSigningOut] = createSignal(false);

  /* The rail is ONE flat list of the active module's items. Our `sections` are
     grouping LABELS, not destinations — Adobe's rail has no headings either —
     so they flatten here and their names survive in the panel's heading. */
  const railItems = createMemo<NavItem[]>(() => {
    const mod = active();
    // NO MODULE OWNS THIS URL (the dashboard). The rail becomes the launcher
    // rather than going empty — which is also what Adobe's rail is at its root:
    // a list of products. Without this the sidebar led nowhere on `/`.
    if (!mod) return props.modules.map((m) => ({ id: m.id, label: m.label, to: m.to, icon: m.icon }));
    return mod.sections.flatMap((sec) => sec.items.filter((i) => !i.hidden));
  });
  /* ON THE MODULE'S LANDING, HOME OWNS THE SELECTION and no area does. CMS and
     FIN give every GROUP the module's own `to` — "Publishing", "Media" and
     "Configuration" are all `/cms` — so the resolver legitimately matches one of
     them there, and the rail lit a group for a page that belongs to the module
     itself. The owner's words: "the sidebar menu icon selected is not home". */
  /* THE ATTRIBUTE, WRITTEN BY US — after the router has had its turn.
     TanStack's Link sets `aria-current="page"` on an exact match and neither
     `activeProps={{}}` nor an explicit "false" displaces it, so on /cms (where
     three groups and the home all carry `to: "/cms"`) a screen reader heard
     FOUR current pages while the rail painted one. A ref plus an effect is the
     same trick @erp/ui/progress-bar uses for `value`: the DOM is corrected once
     the framework has finished writing to it, and the accessible state matches
     what the eye sees. */
  const markCurrent = (el: HTMLElement, isCurrent: () => boolean) => {
    createEffect(() => {
      if (isCurrent()) el.setAttribute("aria-current", "page");
      else el.removeAttribute("aria-current");
    });
  };

  const onModuleLanding = () => props.pathname === active()?.to;
  const railActive = (item: NavItem) =>
    !onModuleLanding() && (activeId() === item.id || (item.children ?? []).some((c) => c.id === activeId()));
  /* THE MODULE'S HOME, first on the rail (2026-10-02, the owner: "a home icon
     here, which is home of the library; in cms it is home of cms"). It points
     at the ACTIVE module's own landing route, so the same glyph means Library
     home under Library and Content home under Content — Adobe's rail opens
     with Home the same way. It is active when nothing else on the rail is,
     which is what "you are on the module's own page" means; no manifest
     carries an item for that route, so there is nothing to collide with. */
  const home = createMemo(() => {
    const mod = active();
    return mod ? { id: `${mod.id}-home`, label: `${mod.label} home`, to: mod.to } : undefined;
  });
  const homeActive = () => Boolean(home()) && !railItems().some(railActive);

  return (
    /* TWO LEVELS, Adobe's (2026-10-02, the owner: "compact with logos, and the
       sub menu after it inside the view, just like adobe.com/files"). Read off
       their product: a 64px rail of icons, and a 240px list beside it that
       exists ONLY when the chosen area has sections — on their /home the rail
       stays and the list is simply not there. That conditional panel is the
       whole mechanism; there is no collapse control to hunt for.

       What this replaces: one 224px column holding a search box, a module
       switcher, every section as an accordion, and the module list at the
       foot. The search is the header's ⌘K (it was a second door to the same
       palette), and switching module is the strip's job now, so both are gone
       rather than duplicated. */
    <nav aria-label="Primary" class={`flex min-h-full ${props.class ?? ""}`}>
      {/* THE RAIL — icons only, which is what makes it compact enough to be
          worth a band. Our labels ("Digital resources", "Acquisitions") cannot
          be set under a 40px glyph the way Adobe's four short ones can, so the
          name lives in the tooltip and the accessible name instead of being
          truncated to "Digit…". Opens RIGHT, into the panel, where there is
          always room. */}
      <div class="flex w-16 shrink-0 flex-col">
        {/* NO OVERFLOW AT ALL, and both halves of that are deliberate.
            `overflow-y:auto` alone forces the OTHER axis to `auto` too (CSS
            overflow rules), so this 56px column — 40px buttons inside 16px of
            padding, exactly 56 — painted a horizontal scrollbar across the foot
            of the rail, which is the slider the owner spotted. Clipping that
            axis fixed the track and broke something worse: the rail is ICONS
            ONLY, its tooltips are the labels, and a clip cuts the bubble off at
            the rail's edge. So the scroll container goes. The rail is CHROME —
            a layer that does not scroll — and its content measured as fitting
            the box exactly at 1440, 1280, 1366 and 1024. */}
        {/* Adobe's rail geometry, measured: 64px column, 16px top and bottom,
            nothing at the sides, and a 10px gap between items (their pitch is
            53+10=63). Ours carries a 40px icon box, so px-3 centres it. */}
        <div class="flex min-h-0 flex-1 flex-col gap-2.5 px-3 py-4">
          <Show when={home()}>
            {(h) => (
              <Link
                to={h().to}
                activeOptions={{ exact: true }}
                activeProps={{}}
                data-tip={h().label}
                aria-label={h().label}
                ref={(el: HTMLElement) => markCurrent(el, homeActive)}
                class={`tooltip tooltip-right flex size-10 shrink-0 items-center justify-center rounded-100 transition-all ${
                  homeActive()
                    ? "bg-(--ui-state-down) text-base-content"
                    : "text-base-muted hover:bg-(--ui-state-hover) focus-visible:bg-(--ui-state-hover) active:bg-(--ui-state-down)"
                }`}
              >
                <Home size={18} />
              </Link>
            )}
          </Show>
          <For each={railItems()}>
            {(item) => (
              <Link
                to={item.to}
                /* `activeProps={{}}` — the router must not decide this at all.
                   `exact` alone was not enough: CMS and FIN give every GROUP
                   the module's own `to` ("Publishing", "Media" and
                   "Configuration" are all `/cms`), so on /cms the router found
                   four exact matches and marked all four current. Emptying
                   `activeProps` drops its automatic aria-current, leaving the
                   kit's resolveActiveItem as the single source — which is the
                   rule this file already stated and only half enforced. */
                activeOptions={{ exact: true }}
                activeProps={{}}
                data-tip={item.label}
                aria-label={item.label}
                ref={(el: HTMLElement) => markCurrent(el, () => railActive(item))}
                class={`tooltip tooltip-right flex size-10 shrink-0 items-center justify-center rounded-100 transition-all ${
                  railActive(item)
                    ? "bg-(--ui-state-down) text-base-content"
                    : "text-base-muted hover:bg-(--ui-state-hover) focus-visible:bg-(--ui-state-hover) active:bg-(--ui-state-down)"
                }`}
              >
                <Show when={item.icon} fallback={<span class="text-sm font-bold">{item.label.charAt(0)}</span>}>
                  <Dynamic component={item.icon!} size={18} />
                </Show>
              </Link>
            )}
          </For>
        </div>
        {/* Account — the source's p-3 footer: profile button + a separate
          sign-out icon that reddens on hover. */}
      <Show when={props.user}>
          {(user) => (
          <div class="shrink-0 border-t border-base-300 p-3">
            <div class="flex items-center gap-2">
              <button
                type="button"
                class="-mx-1 flex min-w-0 flex-1 items-center gap-2 rounded-300 px-1 py-1 text-left transition-colors hover:bg-(--ui-state-hover) focus-visible:bg-(--ui-state-hover) active:bg-(--ui-state-down)"
                title="View your profile"
                aria-label={`Account menu — ${user().name}`}
                onClick={() => props.onProfile?.()}
              >
                {/* Fallback hue = avatarTone's name hash — the SAME pair this
                    person gets from AvatarPhoto on every other screen, one of
                    the nine categorization hues (identity, never status). */}
                <span
                  class={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${avatarTone(user().name)}`}
                >
                  {initials(user().name)}
                </span>
                <span class="min-w-0 flex-1">
                  <span class="block truncate text-xs font-bold text-base-content">{user().name}</span>
                  {/* One line when the account has no display name and the
                      portal fell back to the address — the audit read
                      "admin.demo@shippr… / admin.demo@shippra…" (UX-16). */}
                  <Show when={user().email && user().email !== user().name}>
                    <span class="block truncate text-xs text-base-subtle">{user().email}</span>
                  </Show>
                </span>
              </button>
              <Show when={props.onSignOut}>
                {/* Box: size-(--ds-target), the component-height-100 hit floor (32px,
                    40 at touch — spectrum.css §8; UX-14 measured this at 21×21). The
                    13px glyph stays; -mr-1 keeps its right edge where the p-3 put it.
                    Fallback = the kit's 28px box for a portal without the skin. */}
                <button
                  type="button"
                  class="-mr-1 flex size-[var(--ds-target,1.75rem)] shrink-0 items-center justify-center rounded-300 text-base-subtle transition-colors hover:bg-error-tint hover:text-error focus-visible:bg-error-tint focus-visible:text-error"
                  title="Sign out"
                  aria-label="Sign out"
                  disabled={signingOut()}
                  aria-busy={signingOut() || undefined}
                  onClick={async () => {
                    setSigningOut(true);
                    try {
                      await props.onSignOut?.();
                    } finally {
                      setSigningOut(false);
                    }
                  }}
                >
                  <Show when={signingOut()} fallback={<LogOut size={13} aria-hidden="true" />}>
                    <span class="loading loading-spinner loading-xs" aria-hidden="true" />
                  </Show>
                </button>
              </Show>
            </div>
          </div>
        )}
        </Show>
      </div>

      {/* The panel is NOT here any more (2026-10-02). Adobe's second band sits
          INSIDE the content region, not in the chrome beside it — measured on
          their DOM, where `.left-panel` is a child of `fw-body` and the rail is
          not. The shell mounts `SidebarPanel` inside <main>; this component is
          the rail, which is all the drawer needs to carry. */}
    </nav>
  );
}
